import { useSuspenseQuery } from '@tanstack/react-query'
import React from 'react'
import { useMount } from 'react-use'
import invariant from 'tiny-invariant'

import CommercialAgreementDetails from '@/components/misc/CommercialAgreementsDetails/CommercialAgreementsDetails'
import NotAllowedToViewContent from '@/components/misc/NotAllowedToViewContent'
import useAppDispatch from '@/hooks/useAppDispatch'
import { usePermissions } from '@/hooks/usePermissions'
import { COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION } from '@/lib/constants'
import { QUERY_KEYS } from '@/lib/queryKeys'
import panelService from '@/lib/services/panelService'
import { showCommercialAgreementsModal } from '@/redux/slices/commercialAgreementsModal'

interface Props {
  panelId: number
}

const CommercialAgreementsTab: React.FC<Props> = ({ panelId }) => {
  const panel = usePanelQuery(panelId).data
  const dispatch = useAppDispatch()
  const { canManageCommercialAgreements } = usePermissions()

  useMount(() => {
    dispatch(
      showCommercialAgreementsModal({
        panelId: panel.id,
        referralDirection: COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION.OUTGOING,
      }),
    )
  })

  return (
    <section className="pb-6">
      {canManageCommercialAgreements ? (
        <CommercialAgreementDetails variant="inline" />
      ) : (
        <NotAllowedToViewContent />
      )}
    </section>
  )
}

function usePanelQuery(panelId?: number) {
  return useSuspenseQuery({
    queryKey: QUERY_KEYS.panel(panelId),
    queryFn: async () => {
      invariant(panelId, 'Expected panelId to be defined')
      return panelService.getPanel(panelId)
    },
    refetchOnWindowFocus: false,
    staleTime: 2000,
  })
}

export default CommercialAgreementsTab
